import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import type { Interaction } from "@/types";
import { hostFromUrl } from "@/utils/formatters";
import { maxStepError, stepForFrame, stepTitle } from "./helpers";
import LogRow from "./LogRow";

type InteractionLogProps = {
  currentFrame: number;
  interaction: Interaction;
  onSelectFrame: (frame: number) => void;
  startUrl: string | null;
};

export default function InteractionLog({ currentFrame, interaction, onSelectFrame, startUrl }: InteractionLogProps) {
  const frames = interaction.screenshots;
  const running = interaction.status === "running";
  const thinkingFrame = Math.max(frames.length - 1, 0);
  const tooltip = "Our AI agent navigated the website to complete the task. This was their thought process.";

  return (
    <div className="w-full shrink-0 lg:w-72">
      <div className="card">
        <div className="card-body box-border max-h-[672px] overflow-y-auto">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="mb-0 text-xl font-semibold text-gray-800">Agent Thoughts</h2>
            <span className="group relative flex items-center">
              <button type="button" className="text-gray-400 hover:text-gray-600" aria-label={tooltip}>
                <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="pointer-events-none absolute right-0 top-7 z-10 hidden w-64 rounded bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-lg group-hover:block group-focus-within:block">
                {tooltip}
              </span>
            </span>
          </div>

          <div className="space-y-3">
            <LogRow
              active={currentFrame === 0}
              completed={frames.length > 0}
              title={startUrl ? `Visited ${hostFromUrl(startUrl)}` : "Visited website"}
              onClick={() => onSelectFrame(0)}
            />

            {frames.map((screenshot, index) => {
              const step = stepForFrame(interaction.steps, frames, index);
              if (!step) {
                return null;
              }

              const active = !running && index === currentFrame;

              return (
                <LogRow
                  key={screenshot.id}
                  active={active}
                  completed={running || index < currentFrame}
                  error={Boolean(step.error_message)}
                  title={stepTitle(step, index)}
                  detail={active ? step.reasoning || step.error_message : null}
                  onClick={() => onSelectFrame(index)}
                />
              );
            })}

            {running ? (
              <LogRow
                active={true}
                completed={false}
                loading={true}
                title="Thinking about next action..."
                detail="Our agent is analyzing the current screen."
                onClick={() => onSelectFrame(thinkingFrame)}
              />
            ) : null}

            {interaction.status === "cancelled" ? (
              <LogRow
                active={currentFrame >= frames.length}
                completed={true}
                title="Finished"
                detail="Manually stopped by user"
                onClick={() => onSelectFrame(Math.max(frames.length - 1, 0))}
              />
            ) : null}

            {maxStepError(interaction) ? (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-800">Maximum steps reached</p>
                <p className="mt-1 text-xs text-amber-700">
                  The interaction stopped as it reached the maximum number of steps allowed.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
